import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bold, Italic, Underline, List, ListOrdered, Clock } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  className
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      onChange(html);
    }
  };

  const executeCommand = (command: string, value?: string) => {
    // Ensure the editor is focused before executing commands
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Use the newer approach for better browser compatibility
      try {
        document.execCommand(command, false, value);
      } catch (error) {
        console.warn('Command execution failed:', command, error);
      }
      
      // Trigger change event after command execution
      setTimeout(() => {
        handleInput();
      }, 10);
    }
  };

  const handleBold = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand('bold');
  };

  const handleItalic = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand('italic');
  };

  const handleUnderline = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand('underline');
  };

  const handleList = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand('insertUnorderedList');
  };

  const handleOrderedList = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand('insertOrderedList');
  };

  const handleInsertTimestamp = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Always insert [MM:SS] as a watermark placeholder
    const watermark = '[MM:SS]';
    
    // Insert the watermark at cursor position
    executeCommand('insertText', watermark);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    executeCommand('insertText', text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          executeCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          executeCommand('underline');
          break;
      }
    }
  };

  return (
    <>
      <style>{`
        .rich-text-editor[contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          font-style: italic;
        }
        .rich-text-editor:focus {
          outline: none;
        }
        .rich-text-editor strong {
          font-weight: bold;
        }
        .rich-text-editor em {
          font-style: italic;
        }
        .rich-text-editor u {
          text-decoration: underline;
        }
        .rich-text-editor ul {
          list-style-type: disc;
          margin-left: 20px;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        .rich-text-editor ol {
          list-style-type: decimal;
          margin-left: 20px;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        .rich-text-editor li {
          margin-bottom: 4px;
        }
        /* Badge style for timestamp watermarks */
        .rich-text-editor:has-text("[MM:SS]") [MM\\:SS] {
          background-color: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          display: inline-block;
          margin: 0 2px;
        }
      `}</style>
      <div className="space-y-2">
        <div className="flex space-x-1 border rounded-md p-1 bg-background">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleBold}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Bold (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleItalic}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Italic (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleUnderline}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Underline (Ctrl+U)"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleList}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleOrderedList}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={handleInsertTimestamp}
            className="h-8 w-8 p-0 hover:bg-accent"
            tabIndex={-1}
            title="Insert Timestamp Watermark [MM:SS]"
          >
            <Clock className="h-4 w-4" />
          </Button>
        </div>
        
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          className={`rich-text-editor min-h-32 max-h-48 overflow-y-auto p-3 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}
          style={{ 
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
      </div>
    </>
  );
};
